/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines-per-function */
import { HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, map, retry, switchMap, throwError, timer } from 'rxjs';

import { AppRoutes } from '../constants/routes';
import { AuthApi } from '../services/authentication/auth-api';
import { SessionStorageService } from '../services/session-storage.service';
import { ToastService } from '../services/toast.service';
import { environment } from '../../../environments/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ITokens } from '../interfaces/token';
import { CurrentUserService } from '../services/authentication/current-user.service';
import { TranslateService } from '@ngx-translate/core';

const EXTERNAL_LOGIN_PATH = '/Auth/external-login?token=';
// eslint-disable-next-line max-statements
export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const toast = inject(ToastService);
  const sessionService = inject(SessionStorageService);
  const currentUser = inject(CurrentUserService);
  const authApi = inject(AuthApi);
  const jwtHelper = inject(JwtHelperService);
  const translate = inject(TranslateService);
  const retryLimit = 5;
  const shouldRetry = (error: HttpErrorResponse) => {
    if (
      error.status === 0 ||
      error.status === 500 ||
      error.status === 501 ||
      error.status === 502 ||
      error.status === 503 ||
      error.status === 504
    ) {
      return timer(1000);
    }
    throw error;
  };

  const getJWTToken = () => {
    const token = sessionService.get('token');
    if (token !== null) {
      return token;
    }
    return '';
  };

  const getBothTokenFromSession = () => {
    const token = sessionService.get('token');
    const refreshToken = sessionService.get('refreshToken');
    if (token !== null && refreshToken !== null) {
      return { token, refreshToken };
    }
    return {} as ITokens;
  };

  const serviceUnavailable = (err: HttpErrorResponse): void => {
    const errorMessage = err?.error?.Message || err?.message;

    const isExternalLogin =
      Boolean(errorMessage) &&
      errorMessage.includes?.(`${environment.API_BASE_URL}${EXTERNAL_LOGIN_PATH}`);
    if (errorMessage) {
      const toastMsg = isExternalLogin ? translate.instant('TOAST.EXTERNAL_LOGIN_API_FAIL') : errorMessage;
      toast.openErrorToast(toastMsg, '', environment.TOAST_DELAY);
    } else {
      toast.openErrorToast(
        translate.instant('TOAST.SERVICE_UNAVAILABLE'),
        '',
        environment.TOAST_DELAY,
      );
    }
  };

  /*
   * =============================
   * TenantId Resolution
   * =============================
   */

  var tenantId = ""
  const host = request.url.split('/')[2];
  const subDomain = host.split('.')[0];

  if (subDomain === "dev" || subDomain === "qa" || subDomain === "stage") {
    tenantId = "meijer"
  } else {
    tenantId = subDomain;
  }

  /*
   * =============================
   * Refresh token request
   * =============================
   */
  if (request.url.includes('RefreshToken')) {
    const transformedReq = request.clone({
      headers: request.headers.set('request-time', new Date().toISOString()),
    });
    return next(transformedReq);
  }

  /*
   * =============================
   * Login request
   * =============================
   */
  if (request.url.includes(AppRoutes.Login)) {
    const transformedReq = request.clone({
      headers: request.headers
      .set('request-time', new Date().toISOString())
      .set('X-Tenant-ID', tenantId),
    });

    return next(transformedReq).pipe(
      map((event: HttpEvent<any>) => event),
      catchError((err: HttpErrorResponse) => {
        const isExternalLogin =
            Boolean(err.message) &&
            err.message.includes?.(`${environment.API_BASE_URL}${EXTERNAL_LOGIN_PATH}`);
        if (isExternalLogin) {
          toast.openErrorToast(translate.instant('TOAST.EXTERNAL_LOGIN_API_FAIL'), '', environment.TOAST_DELAY);
        } else if (err.status === 203) {
          toast.openErrorToast(
            translate.instant('TOAST.INVALID_USERNAME_PASSWORD'),
            '',
            environment.TOAST_DELAY,
          );
        } else if (err.status === 400 && request.url.indexOf(err.error.Password)) {
          toast.openErrorToast(err?.error?.Message || err?.message, '', environment.TOAST_DELAY);
        } else if (err.status === 400 && request.url.indexOf('RefreshToken') < 1) {
          toast.openErrorToast(
            translate.instant('TOAST.USER_IS_INACTIVE'),
            '',
            environment.TOAST_DELAY,
          );
        } else if (err.status === 409 && err.error.Message === 'ADAS-HMI user already exists.') {
          toast.openErrorToast(
            translate.instant('TOAST.USER_ALREADY_EXIST'),
            '',
            environment.TOAST_DELAY,
          );
        } else if (err.status === 500) {
          toast.openErrorToast(
            `${translate.instant('TOAST.INTERNAL_SERVER_ERROR')}: ${err.error.Message || err?.message}`,
            '',
            environment.TOAST_DELAY,
          );
        } else if (err.status === 403 || err.status === 502) {
          serviceUnavailable(err);
        } else if (err.status === 401 || err.message) {
          toast.openErrorToast(err.error.Message || err?.message, '', environment.TOAST_DELAY);
        }
        return throwError(() => err);
      }),
    );
  }

  /*
   * =============================
   * Normal API calls
   * =============================
   */
  const token = getJWTToken();
  const isTokenExpired = jwtHelper.isTokenExpired(token);

  let transformedReq = request;

  if (!isTokenExpired) {
    transformedReq = request.clone({
      headers: request.headers
        .set('Authorization', `bearer ${token}`)
        .set('request-time', new Date().toISOString())
        .set('X-Tenant-ID', tenantId),
    });
  } else if (token && isTokenExpired) {
    const savedToken = getBothTokenFromSession();

    return authApi.refreshToken(savedToken).pipe(
      switchMap((newTokens) => {
        if (
          newTokens.success === true &&
          newTokens.token !== null &&
          newTokens.refreshToken !== null
        ) {
          sessionService.set('token', newTokens.token);
          sessionService.set('refreshToken', newTokens.refreshToken);

          transformedReq = request.clone({
            headers: request.headers
              .set('Authorization', `bearer ${newTokens.token}`)
              .set('request-time', new Date().toISOString())
              .set('X-Tenant-ID', tenantId),
          });

          // AuthService.getTokenDecoded(newTokens.token);
        } else {
          currentUser.logout.next(true);
        }

        return next(transformedReq);
      }),
    );
  } else {
    transformedReq = request.clone({
      headers: request.headers
      .set('request-time', new Date().toISOString())
      .set('X-Tenant-ID', tenantId),
    });
  }

  return next(transformedReq).pipe(
    retry({ count: retryLimit, delay: shouldRetry }),
    map((event: HttpEvent<unknown>) => event),
    catchError((err: HttpErrorResponse) => {
      if (err.status === 400) {
        if (err.url?.endsWith('RoverCommands/resetmrm') && Boolean(err.error.Message)) {
          toast.openErrorToast(err.error.Message || err?.message, '', environment.TOAST_DELAY);
        } else if (err.url?.endsWith('RoverCommands/switch') && Boolean(err.error.Message)) {
          toast.openErrorToast(err.error.Message || err?.message, '', environment.TOAST_DELAY);
        } else if (err.url?.includes('PowerCommands/shutdown') && Boolean(err.error)) {
          toast.openErrorToast(
            JSON.parse(err.error).Message || err?.message,
            '',
            environment.TOAST_DELAY,
          );
        } else if (err.url?.includes('PowerCommands/launch') && Boolean(err.error)) {
          toast.openErrorToast(
            JSON.parse(err.error).Message || err?.message,
            '',
            environment.TOAST_DELAY,
          );
        }
      } else if (err.status === 500) {
        toast.openErrorToast(
          `${translate.instant('TOAST.INTERNAL_SERVER_ERROR')}: ${err.error.Message || err?.message}`,
          '',
          environment.TOAST_DELAY,
        );
      } else if (err.status === 403 || err.status === 502) {
        serviceUnavailable(err);
      }

      return throwError(() => err);
    }),
  );
};
